import React, { useState, useEffect, useMemo } from 'react';
import { api, loadBuildingsForSelector } from '../api';
import { paymentApi, Transaction, TransactionQuery, ONLINE_RESIDENT_COLLECTOR_ID } from '../api/paymentApi';
import { getStaffMembers } from '../api/tasksApi';
import { BillDetailsModal } from '../components/modals/BillDetailsModal';
import { TransactionBillEditModal } from '../components/modals/TransactionBillEditModal';
import { TransactionBillCancelModal } from '../components/modals/TransactionBillCancelModal';
import { TransactionReportHistoryTab } from './TransactionReportHistoryTab';
import { CasualParkingVoucherReportTab } from './CasualParkingVoucherReportTab';
import { TransactionReportSubLedgerTab } from './TransactionReportSubLedgerTab';
import { TransactionReportParkingRevenueTab } from './TransactionReportParkingRevenueTab';
import { downloadExcel, sanitizeExportFilename } from '../utils/excelExport';
import { formatLocalDateString } from '../utils/advance-booking-days.util';
import { useAuth } from '../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../utils/buildingSelection';
import toast from 'react-hot-toast';
import { settingsApi } from '../services/settingsApi';
import { isPaymentGatewaySandboxEnv } from '../utils/paymentGateway.util';

interface Building {
  id: string;
  name: string;
}

interface StaffOption {
  id: string;
  name: string;
  email: string;
}

/** Paid bills have receiptNumber; pending online bills use numeric billId (order code). */
function getTransactionReceiptOrBillCode(transaction: Transaction): string {
  const receipt = transaction.receiptNumber?.trim();
  if (receipt) return receipt;

  const billCode = transaction.billId?.trim();
  if (billCode) return billCode;

  const code = transaction.transactionCode?.trim();
  if (code) return code;

  return '—';
}

function isShowingBillCodeInsteadOfReceipt(transaction: Transaction): boolean {
  return !transaction.receiptNumber?.trim() && !!transaction.billId?.trim();
}

const PAYMENT_METHOD_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: 'CASH', label: 'Tiền mặt' },
  { value: 'BANK_TRANSFER', label: 'Chuyển khoản' },
  { value: 'VNPAY', label: 'VNPay' },
  { value: 'MOMO', label: 'Ví Momo' },
  { value: 'VIETQR', label: 'VietQR' },
  { value: 'SHINHAN', label: 'Shinhan Bank' },
] as const;

const PAYMENT_METHOD_SUMMARY_ORDER = [
  'CASH',
  'BANK_TRANSFER',
  'VNPAY',
  'MOMO',
  'VIETQR',
  'SHINHAN',
  'MB_PAYGATE',
  'MBB',
] as const;

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: 'Tiền mặt',
  BANK_TRANSFER: 'Chuyển khoản',
  VNPAY: 'VNPay',
  MOMO: 'Ví Momo',
  VIETQR: 'VietQR',
  SHINHAN: 'Shinhan Bank',
  MB_PAYGATE: 'MB Paygate',
  MBB: 'MB Bank',
};

function paymentMethodLabel(method: string) {
  const normalized = (method || '').toUpperCase();
  return PAYMENT_METHOD_LABELS[normalized] || method || '—';
}

function summarizeAmountsByPaymentMethod(items: Transaction[]) {
  const totals = new Map<string, number>();
  for (const item of items) {
    const key = (item.paymentMethod || 'UNKNOWN').toUpperCase();
    totals.set(key, (totals.get(key) ?? 0) + item.amount);
  }
  const summaries: Array<{ key: string; label: string; amount: number }> = [];
  for (const key of PAYMENT_METHOD_SUMMARY_ORDER) {
    const amount = totals.get(key);
    if (amount != null) {
      summaries.push({ key, label: paymentMethodLabel(key), amount });
      totals.delete(key);
    }
  }
  for (const [key, amount] of Array.from(totals.entries())) {
    summaries.push({ key, label: paymentMethodLabel(key), amount });
  }
  return summaries;
}

export const TransactionReport: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const [canCancelBills, setCanCancelBills] = useState(false);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [selectedStaffId, setSelectedStaffId] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [includeUnpaid, setIncludeUnpaid] = useState<boolean>(false);
  const [dateFilter, setDateFilter] = useState<'paid' | 'created'>('paid');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [billStatusFilter, setBillStatusFilter] = useState<'PAID' | 'CANCELLED' | 'ALL'>('PAID');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBillId, setSelectedBillId] = useState<string>('');
  const [showBillDetails, setShowBillDetails] = useState(false);
  const [simulatingBillId, setSimulatingBillId] = useState<string | null>(null);
  const [canEditTransactions, setCanEditTransactions] = useState(false);
  const [editTransaction, setEditTransaction] = useState<Transaction | null>(null);
  const [cancelTransaction, setCancelTransaction] = useState<Transaction | null>(null);
  const [reportTab, setReportTab] = useState<
    'list' | 'parking' | 'history' | 'vouchers' | 'subledger'
  >('list');
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [gatewayEnvironments, setGatewayEnvironments] = useState<
    Partial<Record<'SHINHAN' | 'VIETQR', string>>
  >({});

  useEffect(() => {
    paymentApi.canEditTransactions().then(setCanEditTransactions).catch(() => setCanEditTransactions(false));
    paymentApi.canCancelTransactions().then(setCanCancelBills).catch(() => setCanCancelBills(false));
  }, []);

  useEffect(() => {
    if (!selectedBuildingId) {
      setGatewayEnvironments({});
      return;
    }
    settingsApi
      .getPaymentGatewaySettings(false, selectedBuildingId)
      .then((rows) => {
        const next: Partial<Record<'SHINHAN' | 'VIETQR', string>> = {};
        for (const row of rows || []) {
          const type = (row.gatewayType || '').toUpperCase();
          if (type === 'SHINHAN' || type === 'VIETQR') {
            next[type] = row.config?.environment as string | undefined;
          }
        }
        setGatewayEnvironments(next);
      })
      .catch(() => setGatewayEnvironments({}));
  }, [selectedBuildingId]);

  useEffect(() => {
    fetchBuildings();
    fetchStaffOptions();
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setFromDate(formatLocalDateString(firstDay));
    setToDate(formatLocalDateString(lastDay));
  }, []);

  const fetchBuildings = async () => {
    try {
      const data = await loadBuildingsForSelector();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch (error) {
      console.error('Error fetching buildings:', error);
      toast.error('Failed to load buildings');
    }
  };

  const fetchStaffOptions = async () => {
    try {
      const staff = await getStaffMembers();
      setStaffOptions(
        (staff || []).map((member) => ({
          id: member.id,
          name: member.name || member.email,
          email: member.email,
        })),
      );
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const handleSearch = async () => {
    if (!selectedBuildingId || !fromDate || !toDate) {
      toast.error('Vui lòng chọn chung cư và khoảng thời gian');
      return;
    }

    setIsLoading(true);
    try {
      const query: TransactionQuery = {
        buildingId: selectedBuildingId,
        fromDate,
        toDate,
        includeUnpaid,
        ...(!includeUnpaid && { dateFilter, billStatus: billStatusFilter }),
        ...(selectedPaymentMethod && { paymentMethod: selectedPaymentMethod }),
        ...(selectedStaffId && { staffUserId: selectedStaffId }),
      };

      const fetched = await paymentApi.getTransactionReport(query);
      let list = fetched || [];
      if (includeUnpaid) {
        list = list.filter((t) => (t.status || '').toUpperCase() === 'PENDING');
      }
      setTransactions(list);
      setShowResults(true);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Lỗi khi tải báo cáo giao dịch');
    } finally {
      setIsLoading(false);
    }
  };

  const getPaymentMethodLabel = paymentMethodLabel;

  const getStatusLabel = (status: string) => {
    const statuses: Record<string, { label: string; className: string }> = {
      PAID: { label: 'Đã thanh toán', className: 'bg-green-100 text-green-800' },
      COMPLETED: { label: 'Đã thanh toán', className: 'bg-green-100 text-green-800' },
      CANCELLED: { label: 'Đã huỷ', className: 'bg-red-100 text-red-800' },
      PENDING: { label: 'Đang xử lý', className: 'bg-yellow-100 text-yellow-800' },
      FAILED: { label: 'Thất bại', className: 'bg-red-100 text-red-800' },
    };
    return statuses[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN').format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const time = date.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${time} ${day}`;
  };

  const filteredTransactions = transactions.filter((transaction) => {
    const term = searchTerm.toLowerCase();
    return (
      (transaction.receiptNumber || '').toLowerCase().includes(term) ||
      (transaction.billId || '').toLowerCase().includes(term) ||
      (transaction.transactionCode || '').toLowerCase().includes(term) ||
      (transaction.apartmentCode || '').toLowerCase().includes(term) ||
      (transaction.staffName || '').toLowerCase().includes(term) ||
      (transaction.notes || '').toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = filteredTransactions.slice(startIndex, endIndex);
  const totalAmount = filteredTransactions.reduce(
    (sum, t) =>
      (t.status || '').toUpperCase() === 'CANCELLED' ? sum : sum + t.amount,
    0,
  );
  const paymentMethodSummaries = useMemo(
    () => summarizeAmountsByPaymentMethod(filteredTransactions),
    [filteredTransactions],
  );

  const amountColumnIndex = 7;
  const trailingColumnCount =
    (canEditTransactions || canCancelBills || (includeUnpaid && isSuperAdmin) ? 1 : 0) + 2;

  const canCancelTransaction = (transaction: Transaction) => {
    if (!canCancelBills) return false;
    const status = (transaction.status || '').toUpperCase();
    if (status !== 'PAID' && status !== 'COMPLETED') return false;
    const method = (transaction.paymentMethod || '').toUpperCase();
    return method === 'CASH' || method === 'BANK_TRANSFER';
  };

  const transactionTotalSummary = (
    <div
      className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg px-3 py-3"
      role="status"
      aria-label="Tổng cộng báo cáo giao dịch"
    >
      <div className="inline-grid grid-cols-[auto_minmax(6.5rem,auto)] items-baseline gap-x-2">
        <span className="font-semibold text-gray-700 text-right">Tổng cộng:</span>
        <span className="font-bold text-green-700 text-lg tabular-nums text-right">
          {formatCurrency(totalAmount)}
        </span>
      </div>
      {paymentMethodSummaries.map((summary) => (
        <div
          key={summary.key}
          className="inline-grid grid-cols-[auto_minmax(6.5rem,auto)] items-baseline gap-x-2 border-l border-yellow-300/80 pl-4"
        >
          <span className="text-sm text-gray-600 text-right">{summary.label}:</span>
          <span className="font-semibold text-gray-900 tabular-nums text-right">
            {formatCurrency(summary.amount)}
          </span>
        </div>
      ))}
    </div>
  );

  const transactionListPagination =
    totalPages > 1 ? (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-gray-700">
          Đang xem {startIndex + 1} đến {Math.min(endIndex, filteredTransactions.length)} trong tổng số{' '}
          {filteredTransactions.length} bản ghi.
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-100 rounded disabled:opacity-50"
          >
            ←
          </button>
          <div className="flex space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
              if (page > totalPages) return null;
              return (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded ${
                    currentPage === page ? 'bg-cyan-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-gray-100 rounded disabled:opacity-50"
          >
            →
          </button>
        </div>
      </div>
    ) : null;

  const handleShowBillDetails = (billId: string) => {
    setSelectedBillId(billId);
    setShowBillDetails(true);
  };

  const handleCloseBillDetails = () => {
    setShowBillDetails(false);
    setSelectedBillId('');
  };

  const canSimulatePayment = (method: string) => {
    const normalized = (method || '').toUpperCase();
    if (!['VIETQR', 'SHINHAN'].includes(normalized)) return false;
    return isPaymentGatewaySandboxEnv(
      gatewayEnvironments[normalized as 'SHINHAN' | 'VIETQR'],
    );
  };

  const handleSimulatePayment = async (transaction: Transaction) => {
    const billId = transaction.billId || transaction.id;
    const method = (transaction.paymentMethod || '').toUpperCase();

    if (!canSimulatePayment(method)) {
      toast.error('Chỉ hỗ trợ giả lập cho VietQR và Shinhan Bank');
      return;
    }

    setSimulatingBillId(billId);
    try {
      if (method === 'SHINHAN') {
        await paymentApi.simulateShinhanPaymentByBill(billId);
      } else {
        await paymentApi.simulateVietqrPaymentByBill(billId);
      }
      toast.success('Giả lập thanh toán thành công');
      await handleSearch();
    } catch (error: any) {
      console.error('Simulate payment failed:', error);
      toast.error(error?.response?.data?.message || 'Không thể giả lập thanh toán');
    } finally {
      setSimulatingBillId(null);
    }
  };

  const handleExport = () => {
    if (filteredTransactions.length === 0) {
      toast.error('Không có dữ liệu để xuất. Vui lòng tìm kiếm trước.');
      return;
    }

    const buildingName = buildings.find((b) => b.id === selectedBuildingId)?.name || 'chung-cu';
    const headers = [
      '#',
      'Mã phiếu thu',
      'Căn hộ',
      'Nhân viên thu',
      'Thời gian tạo',
      'Thời gian thu',
      'Hình thức',
      'Số tiền',
      'Trạng thái',
      'Ghi chú',
    ];

    const dataRows = filteredTransactions.map((transaction, index) => [
      index + 1,
      getTransactionReceiptOrBillCode(transaction),
      transaction.apartmentCode,
      transaction.staffName || '',
      formatDate(transaction.createdAt ?? transaction.transactionDate),
      transaction.paidAt ? formatDate(transaction.paidAt) : '—',
      getPaymentMethodLabel(transaction.paymentMethod),
      transaction.amount,
      getStatusLabel(transaction.status).label,
      transaction.notes || '',
    ]);

    dataRows.push(['', '', '', '', '', '', 'Tổng cộng', totalAmount, '', '']);
    for (const summary of paymentMethodSummaries) {
      dataRows.push(['', '', '', '', '', '', summary.label, summary.amount, '', '']);
    }

    const fromLabel = new Date(fromDate).toLocaleDateString('vi-VN').replace(/\//g, '-');
    const toLabel = new Date(toDate).toLocaleDateString('vi-VN').replace(/\//g, '-');
    const filename = `bao-cao-giao-dich-${sanitizeExportFilename(buildingName)}-${fromLabel}-${toLabel}.xlsx`;
    downloadExcel('Báo cáo giao dịch', headers, dataRows, filename, {
      rightAlignColumnIndices: [7],
    });
    toast.success('Đã xuất file Excel');
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <nav className="flex mb-6 text-sm">
        <span className="text-gray-500">🏠</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">PHÍ DỊCH VỤ</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">Báo cáo</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-cyan-600 font-medium">Giao dịch</span>
      </nav>

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Báo cáo giao dịch</h1>
        {selectedBuildingId ? (
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm flex-wrap">
            <button
              type="button"
              onClick={() => setReportTab('list')}
              className={`px-4 py-2 ${reportTab === 'list' ? 'bg-cyan-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Danh sách
            </button>
            <button
              type="button"
              onClick={() => setReportTab('parking')}
              className={`px-4 py-2 border-l ${reportTab === 'parking' ? 'bg-cyan-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Doanh thu xe
            </button>
            <button
              type="button"
              onClick={() => setReportTab('history')}
              className={`px-4 py-2 border-l ${reportTab === 'history' ? 'bg-cyan-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Lịch sử
            </button>
            <button
              type="button"
              onClick={() => setReportTab('vouchers')}
              className={`px-4 py-2 border-l ${reportTab === 'vouchers' ? 'bg-cyan-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Voucher xe vãng lai
            </button>
            <button
              type="button"
              onClick={() => setReportTab('subledger')}
              className={`px-4 py-2 border-l ${reportTab === 'subledger' ? 'bg-cyan-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Sổ phụ tài khoản
            </button>
          </div>
        ) : null}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-wrap gap-x-4 gap-y-4 mb-4 items-end">
          {shouldShowBuildingSelector(buildings) && (
            <div className="w-full sm:w-auto sm:min-w-[200px] sm:max-w-[240px] flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Chung cư</label>
              <select
                value={selectedBuildingId}
                onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="">Chọn chung cư</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="w-full sm:w-auto sm:min-w-[140px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">Từ ngày</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          <div className="w-full sm:w-auto sm:min-w-[140px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">Đến ngày</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          <div className="w-full sm:w-auto sm:min-w-[160px] sm:max-w-[220px] flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Nhân viên thu</label>
            <select
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option value="">Tất cả nhân viên</option>
              <option value={ONLINE_RESIDENT_COLLECTOR_ID}>Cư dân (Online)</option>
              {staffOptions.map((staff) => (
                <option key={staff.id} value={staff.id}>
                  {staff.name}
                </option>
              ))}
            </select>
          </div>

          {!includeUnpaid && (
            <div className="w-full sm:w-auto sm:min-w-[140px] sm:max-w-[180px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Trạng thái</label>
              <select
                value={billStatusFilter}
                onChange={(e) =>
                  setBillStatusFilter(e.target.value as 'PAID' | 'CANCELLED' | 'ALL')
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="PAID">Đã thanh toán</option>
                <option value="CANCELLED">Đã huỷ</option>
                <option value="ALL">Tất cả</option>
              </select>
            </div>
          )}
        </div>

        <div className="mb-4">
          <div className="flex flex-wrap items-center gap-4">
            {!includeUnpaid ? (
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-gray-700">Lọc Từ–Đến theo:</span>
                <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="dateFilter"
                    checked={dateFilter === 'paid'}
                    onChange={() => setDateFilter('paid')}
                    className="text-cyan-600 focus:ring-cyan-500"
                  />
                  Ngày thu
                </label>
                <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="dateFilter"
                    checked={dateFilter === 'created'}
                    onChange={() => setDateFilter('created')}
                    className="text-cyan-600 focus:ring-cyan-500"
                  />
                  Ngày tạo
                </label>
              </div>
            ) : (
              <span className="text-sm text-gray-500">Khoảng ngày áp dụng theo ngày tạo (chưa thanh toán).</span>
            )}

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeUnpaid}
                onChange={(e) => setIncludeUnpaid(e.target.checked)}
                className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
              />
              <span className="ml-2 text-sm text-gray-700">Xem cả các giao dịch chưa thanh toán</span>
            </label>
            <p className="text-xs text-gray-500 w-full sm:w-auto sm:max-w-xl">
              Phiếu thanh toán Shinhan / VietQR từ app cư dân (chờ ngân hàng) chỉ hiển thị khi bật mục này;
              lọc theo <strong>ngày tạo</strong> phiếu.
            </p>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Hình thức:</span>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {PAYMENT_METHOD_FILTER_OPTIONS.map((option) => (
                  <label key={option.value || 'all'} className="flex items-center">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={option.value}
                      checked={selectedPaymentMethod === option.value}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      className="mr-1"
                    />
                    <span className="text-sm whitespace-nowrap">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleSearch}
            disabled={isLoading}
            className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Đang tìm...' : 'TÌM KIẾM'}
          </button>
        </div>
      </div>

      {showResults && reportTab === 'parking' && selectedBuildingId && fromDate && toDate && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <TransactionReportParkingRevenueTab
            buildingId={selectedBuildingId}
            fromDate={fromDate}
            toDate={toDate}
          />
        </div>
      )}

      {reportTab === 'vouchers' && selectedBuildingId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <CasualParkingVoucherReportTab buildingId={selectedBuildingId} />
        </div>
      )}

      {reportTab === 'subledger' && selectedBuildingId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <TransactionReportSubLedgerTab buildingId={selectedBuildingId} />
        </div>
      )}

      {showResults && reportTab === 'history' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <TransactionReportHistoryTab
            key={historyRefreshKey}
            buildingId={selectedBuildingId}
            fromDate={fromDate}
            toDate={toDate}
            onOpenBill={handleShowBillDetails}
          />
        </div>
      )}

      {showResults && reportTab === 'list' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Danh sách giao dịch từ {new Date(fromDate).toLocaleDateString('vi-VN')} đến{' '}
            {new Date(toDate).toLocaleDateString('vi-VN')}
            {selectedStaffId && (
              <span className="text-base font-medium text-gray-600">
                {' '}
                —{' '}
                {selectedStaffId === ONLINE_RESIDENT_COLLECTOR_ID
                  ? 'Cư dân (Online)'
                  : staffOptions.find((s) => s.id === selectedStaffId)?.name}
              </span>
            )}
          </h2>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-700 whitespace-nowrap">Số bản ghi/trang</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border border-gray-200 rounded-lg"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700 whitespace-nowrap">Tìm kiếm:</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Mã phiếu thu, mã hóa đơn, căn hộ..."
                  className="px-3 py-1 border border-gray-200 rounded-lg min-w-[220px]"
                />
              </div>
              <button
                onClick={handleExport}
                disabled={filteredTransactions.length === 0}
                className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Xuất Excel
              </button>
            </div>
          </div>

          <div className="space-y-3 mb-4">
            {transactionTotalSummary}
            {transactionListPagination}
          </div>

          {filteredTransactions.length === 0 && (
            <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {includeUnpaid
                ? 'Không có phiếu chờ thanh toán trong khoảng ngày đã chọn (thử mở rộng Từ–Đến hoặc bỏ lọc hình thức).'
                : 'Không có phiếu đã thanh toán trong khoảng này. Để xem phiếu Shinhan/VietQR đang chờ ngân hàng, bật «Xem cả các giao dịch chưa thanh toán» và tìm lại.'}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full w-max border-collapse border border-gray-200 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-gradient-to-r from-cyan-50 to-blue-50">
                  <th className="border-b border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap w-12">#</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Mã phiếu thu</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Căn hộ</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Nhân viên thu</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Thời gian tạo</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Thời gian thu</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Hình thức</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-right text-sm font-semibold text-gray-700 whitespace-nowrap">Số tiền</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Trạng thái</th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap min-w-[200px]">Ghi chú</th>
                  {(canEditTransactions || canCancelBills || (includeUnpaid && isSuperAdmin)) && (
                    <th className="border-b border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Thao tác</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentTransactions.map((transaction, index) => {
                  const status = getStatusLabel(transaction.status);
                  return (
                    <tr
                      key={transaction.id}
                      className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                    >
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{startIndex + index + 1}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => handleShowBillDetails(transaction.id)}
                          className="text-cyan-600 hover:text-cyan-800 font-semibold text-sm underline cursor-pointer transition-colors whitespace-nowrap tabular-nums"
                          title={
                            isShowingBillCodeInsteadOfReceipt(transaction)
                              ? `Mã hóa đơn (chưa có phiếu thu): ${transaction.billId}`
                              : transaction.receiptNumber || undefined
                          }
                        >
                          {getTransactionReceiptOrBillCode(transaction)}
                        </button>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="font-medium text-gray-900 text-sm">{transaction.apartmentCode}</span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{transaction.staffName || '—'}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(transaction.createdAt ?? transaction.transactionDate)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">
                        {transaction.paidAt ? formatDate(transaction.paidAt) : '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
                          {getPaymentMethodLabel(transaction.paymentMethod)}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <span className="font-semibold text-green-600 tabular-nums">
                          {formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${status.className}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap max-w-[280px] truncate" title={transaction.notes || ''}>
                        {transaction.notes || '-'}
                      </td>
                      {(canEditTransactions || canCancelBills || (includeUnpaid && isSuperAdmin)) && (
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex flex-wrap gap-1">
                            {canEditTransactions &&
                              transaction.editable &&
                              ['PAID', 'COMPLETED'].includes((transaction.status || '').toUpperCase()) && (
                                <button
                                  type="button"
                                  onClick={() => setEditTransaction(transaction)}
                                  className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-md text-xs font-medium"
                                >
                                  Sửa
                                </button>
                              )}
                            {canCancelTransaction(transaction) && (
                              <button
                                type="button"
                                onClick={() => setCancelTransaction(transaction)}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs font-medium"
                              >
                                Huỷ
                              </button>
                            )}
                            {includeUnpaid &&
                              isSuperAdmin &&
                              transaction.status?.toUpperCase() === 'PENDING' &&
                              canSimulatePayment(transaction.paymentMethod) && (
                                <button
                                  onClick={() => handleSimulatePayment(transaction)}
                                  disabled={simulatingBillId === (transaction.billId || transaction.id)}
                                  className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-xs font-medium disabled:opacity-50"
                                >
                                  {simulatingBillId === (transaction.billId || transaction.id)
                                    ? 'Đang giả lập...'
                                    : 'Giả lập TT'}
                                </button>
                              )}
                            {!canEditTransactions &&
                              !canCancelTransaction(transaction) &&
                              !(includeUnpaid && isSuperAdmin && transaction.status?.toUpperCase() === 'PENDING') && (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gradient-to-r from-yellow-50 to-orange-50 border-t-2 border-yellow-200">
                  <td
                    colSpan={amountColumnIndex}
                    className="px-3 py-3 text-right font-semibold text-gray-700 whitespace-nowrap"
                  >
                    Tổng cộng:
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-right">
                    <span className="font-bold text-green-700 text-lg tabular-nums">
                      {formatCurrency(totalAmount)}
                    </span>
                  </td>
                  <td colSpan={trailingColumnCount} className="px-3 py-3"></td>
                </tr>
                {paymentMethodSummaries.map((summary) => (
                  <tr key={summary.key} className="bg-yellow-50/60 border-t border-yellow-100">
                    <td
                      colSpan={amountColumnIndex}
                      className="px-3 py-2 text-right text-sm text-gray-600 whitespace-nowrap"
                    >
                      {summary.label}:
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <span className="font-semibold text-gray-900 tabular-nums">
                        {formatCurrency(summary.amount)}
                      </span>
                    </td>
                    <td colSpan={trailingColumnCount} className="px-3 py-2"></td>
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>

          {transactionListPagination && <div className="mt-4">{transactionListPagination}</div>}
        </div>
      )}

      <BillDetailsModal isOpen={showBillDetails} onClose={handleCloseBillDetails} billId={selectedBillId} />
      <TransactionBillEditModal
        isOpen={Boolean(editTransaction)}
        transaction={editTransaction}
        onClose={() => setEditTransaction(null)}
        onSaved={async () => {
          await handleSearch();
          setHistoryRefreshKey((k) => k + 1);
        }}
      />
      <TransactionBillCancelModal
        isOpen={Boolean(cancelTransaction)}
        transaction={cancelTransaction}
        onClose={() => setCancelTransaction(null)}
        onCancelled={async () => {
          await handleSearch();
          setHistoryRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
};
